import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { Link } from 'react-router'
import type { ClaimCode } from '../../../api/accounts'
import { api } from '../../../api/client'
import { ClaimCodeNotice } from './ClaimCodeNotice'

interface Created {
	id: string
	supportId: string
	claim: ClaimCode
}

/** Creates an account for someone who hasn't bought (press, beta, comps) and hands back its claim code. */
export function NewAccountForm() {
	const client = useQueryClient()
	const [note, setNote] = useState('')
	const [days, setDays] = useState('90')
	const create = useMutation({
		mutationFn: () => {
			const grant =
				days === ''
					? undefined
					: {
							plan: 'pro',
							expiresAt: days === 'forever' ? null : new Date(Date.now() + Number(days) * 86_400_000).toISOString(),
						}
			return api.post<Created>('/api/v1/admin/accounts', {
				...(note.trim() ? { note: note.trim() } : {}),
				...(grant ? { grant } : {}),
			})
		},
		onSuccess: () => {
			setNote('')
			client.invalidateQueries({ queryKey: ['admin-accounts'] })
		},
	})
	const submit = (e: FormEvent) => {
		e.preventDefault()
		create.mutate()
	}

	return (
		<section className="card stack">
			<h2 style={{ margin: 0 }}>New account</h2>
			<form className="row" style={{ flexWrap: 'wrap' }} onSubmit={submit}>
				<input
					aria-label="Note"
					placeholder="Who is it for?"
					value={note}
					onChange={(e) => setNote(e.target.value)}
					style={{ flex: 1, minWidth: '12rem' }}
				/>
				<select aria-label="Pro access" value={days} onChange={(e) => setDays(e.target.value)}>
					<option value="">No access yet</option>
					<option value="30">Pro for 30 days</option>
					<option value="90">Pro for 90 days</option>
					<option value="365">Pro for a year</option>
					<option value="forever">Pro, no end date</option>
				</select>
				<button type="submit" className="button" disabled={create.isPending}>
					Create
				</button>
			</form>
			{create.isError && <p className="error small">{create.error.message}</p>}
			{create.data && (
				<div className="stack">
					<p className="small" style={{ margin: 0 }}>
						Created{' '}
						<Link to={`/admin/accounts/${create.data.id}`} className="mono">
							{create.data.supportId}
						</Link>
						.
					</p>
					<ClaimCodeNotice claim={create.data.claim} />
				</div>
			)}
		</section>
	)
}
